import React from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControlLabel, IconButton, LinearProgress, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
  MenuItem,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { getApplications } from '../../services/admin/applicationsApi'
import { createPermission, deletePermission, getPermissions, updatePermission } from '../../services/admin/permissionsApi'
import { getApiErrorMessage, getPaginatedItems } from '../../utils/apiData'
import { SortableHeader } from '../../components/tables/SortableHeader'
import { sortTableRows, toggleTableSort } from '../../utils/tableSorting'

const emptyForm = { applicationId: '', code: '', name: '', description: '', active: true }

const applicationIdOf = (permission) => String(permission.application_id ?? permission.applicationId ?? '')

const loadAll = async (request) => {
  const first = await request({ page: 1, limit: 500, sortDirection: 'asc' })
  const items = getPaginatedItems(first)
  for (let page = 2; page <= (first.meta?.totalPages || 1); page += 1) {
    items.push(...getPaginatedItems(await request({ page, limit: 500, sortDirection: 'asc' })))
  }
  return items
}

export const AdminPermissions = ({ canRead, canWrite }) => {
  const [permissions, setPermissions] = React.useState([])
  const [applications, setApplications] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [success, setSuccess] = React.useState('')
  const [search, setSearch] = React.useState('')
  const [applicationFilter, setApplicationFilter] = React.useState('')
  const [sort, setSort] = React.useState({ column: 'code', direction: 'asc' })
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(null)
  const [deleting, setDeleting] = React.useState(null)
  const [form, setForm] = React.useState(emptyForm)
  const [formError, setFormError] = React.useState('')
  const [saving, setSaving] = React.useState(false)

  const refresh = React.useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [permissionItems, applicationItems] = await Promise.all([
        loadAll(getPermissions),
        loadAll(getApplications),
      ])
      setPermissions(permissionItems)
      setApplications(applicationItems)
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudieron cargar los permisos.'))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (canRead) refresh()
  }, [canRead, refresh])

  const applicationNames = React.useMemo(() => new Map(
    applications.map((application) => [String(application.id), application.name || application.code]),
  ), [applications])

  const visiblePermissions = React.useMemo(() => permissions.filter((permission) => {
    const applicationId = applicationIdOf(permission)
    if (applicationFilter && applicationId !== applicationFilter) return false
    const text = `${permission.code} ${permission.name} ${permission.description || ''} ${applicationNames.get(applicationId) || ''}`.toLowerCase()
    return text.includes(search.trim().toLowerCase())
  }), [permissions, applicationFilter, applicationNames, search])

  const sortedPermissions = React.useMemo(() => sortTableRows(visiblePermissions, sort.column, sort.direction, {
    name: (permission) => permission.name,
    code: (permission) => permission.code,
    application: (permission) => applicationNames.get(applicationIdOf(permission)) || applicationIdOf(permission),
    status: (permission) => permission.active === false ? 0 : 1,
  }), [visiblePermissions, sort, applicationNames])

  const openCreate = () => {
    setEditing(null)
    setForm({ ...emptyForm, applicationId: applicationFilter })
    setFormError('')
    setDialogOpen(true)
  }

  const openEdit = (permission) => {
    setEditing(permission)
    setForm({
      applicationId: applicationIdOf(permission),
      code: permission.code || '',
      name: permission.name || '',
      description: permission.description || '',
      active: permission.active !== false,
    })
    setFormError('')
    setDialogOpen(true)
  }

  const closeDialog = () => {
    if (!saving) setDialogOpen(false)
  }

  const save = async (event) => {
    event.preventDefault()
    if (!canWrite || saving) return
    setFormError('')
    setSaving(true)
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim(),
        active: form.active,
        ...(!editing ? { applicationId: form.applicationId } : {}),
      }
      if (editing) await updatePermission(editing.id, payload)
      else await createPermission(payload)
      setDialogOpen(false)
      setSuccess(editing ? 'Permiso actualizado.' : 'Permiso creado.')
      await refresh()
    } catch (requestError) {
      setFormError(getApiErrorMessage(requestError, 'No se pudo guardar el permiso.'))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!canWrite || !deleting || saving) return
    setSaving(true)
    setError('')
    try {
      await deletePermission(deleting.id)
      setDeleting(null)
      setSuccess('Permiso eliminado.')
      await refresh()
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'No se pudo eliminar el permiso. Puede estar asignado a roles o usuarios.'))
      setDeleting(null)
    } finally {
      setSaving(false)
    }
  }

  if (!canRead) return <Alert severity="warning">No tienes permiso para consultar el catálogo de permisos.</Alert>

  return (
    <Box className="dashboard-section-card admin-panel-card admin-page-section">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 2 }}>
        <Typography className="section-title">Permisos configurados ({visiblePermissions.length})</Typography>
        {canWrite && <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>Nuevo permiso</Button>}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField size="small" label="Buscar permiso" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: 240 }} />
        <TextField select size="small" label="Aplicación" value={applicationFilter} onChange={(event) => setApplicationFilter(event.target.value)} sx={{ minWidth: 240 }}>
          <MenuItem value="">Todas</MenuItem>
          {applications.map((application) => <MenuItem key={application.id} value={String(application.id)}>{application.name || application.code}</MenuItem>)}
        </TextField>
      </Stack>
      {loading && <LinearProgress sx={{ mb: 1.5 }} />}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 1.5 }}>{success}</Alert>}
      <Box className="activity-table-wrap">
        <Table size="small">
          <TableHead><TableRow>
            <SortableHeader column="name" label="Permiso" sort={sort} onSort={(column) => setSort((current) => toggleTableSort(current, column))} />
            <SortableHeader column="code" label="Código" sort={sort} onSort={(column) => setSort((current) => toggleTableSort(current, column))} />
            <SortableHeader column="application" label="Aplicación" sort={sort} onSort={(column) => setSort((current) => toggleTableSort(current, column))} />
            <SortableHeader column="status" label="Estado" sort={sort} onSort={(column) => setSort((current) => toggleTableSort(current, column))} />
            {canWrite && <TableCell align="right">Acciones</TableCell>}
          </TableRow></TableHead>
          <TableBody>
            {sortedPermissions.map((permission) => <TableRow key={permission.id} hover>
              <TableCell><Typography>{permission.name}</Typography><Typography className="admin-table-subtext">{permission.description || 'Sin descripción'}</Typography></TableCell>
              <TableCell>{permission.code}</TableCell>
              <TableCell>{applicationNames.get(applicationIdOf(permission)) || applicationIdOf(permission)}</TableCell>
              <TableCell><Chip size="small" className={`admin-status-chip ${permission.active !== false ? 'admin-status-success' : 'suite-status-chip-muted'}`} label={permission.active !== false ? 'Activo' : 'Inactivo'} /></TableCell>
              {canWrite && <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="Editar permiso"><IconButton size="small" aria-label={`Editar ${permission.code}`} onClick={() => openEdit(permission)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Eliminar permiso"><IconButton size="small" color="error" aria-label={`Eliminar ${permission.code}`} onClick={() => setDeleting(permission)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></Tooltip>
              </TableCell>}
            </TableRow>)}
            {!loading && visiblePermissions.length === 0 && <TableRow><TableCell colSpan={canWrite ? 5 : 4}>No hay permisos para este filtro.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={dialogOpen && canWrite} onClose={closeDialog} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={save}>
          <DialogTitle>{editing ? 'Editar permiso' : 'Nuevo permiso'}</DialogTitle>
          <DialogContent dividers><Stack spacing={2} sx={{ pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField select label="Aplicación" value={form.applicationId} required disabled={Boolean(editing)} onChange={(event) => setForm({ ...form, applicationId: event.target.value })}>
              {applications.map((application) => <MenuItem key={application.id} value={String(application.id)}>{application.name || application.code}</MenuItem>)}
            </TextField>
            <TextField label="Código" value={form.code} required slotProps={{ htmlInput: { maxLength: 150, pattern: '[A-Za-z0-9_]+' } }} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} />
            <TextField label="Nombre" value={form.name} required slotProps={{ htmlInput: { maxLength: 180 } }} onChange={(event) => setForm({ ...form, name: event.target.value })} />
            <TextField label="Descripción" value={form.description} multiline minRows={2} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            <FormControlLabel control={<Switch checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} />} label="Activo" />
          </Stack></DialogContent>
          <DialogActions><Button onClick={closeDialog} disabled={saving}>Cancelar</Button><Button type="submit" variant="contained" disabled={saving || !form.applicationId || !form.code.trim() || !form.name.trim()}>{saving ? 'Guardando...' : 'Guardar'}</Button></DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(deleting) && canWrite} onClose={() => !saving && setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar permiso</DialogTitle>
        <DialogContent><DialogContentText>¿Eliminar {deleting?.code}? Esta acción no se puede deshacer.</DialogContentText></DialogContent>
        <DialogActions><Button onClick={() => setDeleting(null)} disabled={saving}>Cancelar</Button><Button color="error" variant="contained" onClick={remove} disabled={saving}>{saving ? 'Eliminando...' : 'Eliminar'}</Button></DialogActions>
      </Dialog>
    </Box>
  )
}
